import { StationeryProduct } from '@shared/utils/print-price/types';
import { CardDelivery } from '../routes/CardCustomizer/CardCustomizer.types';
import { StationeryPaperSize } from '../routes/CardCustomizer/steps/CardDesign/config';
import { ProductUIConfig } from './config.types';
import { StationeryPrintOrderVendorEnum } from '@graphql/generated';

const A2_ENVELOPE_SKU = 'WJYOEA2W';
const A7_ENVELOPE_SKU = 'WJYOEA7W';

export const FREE_DIGITAL_DUMMY_PRODUCT: StationeryProduct = {
  id: 'FREEDIGITALDUMMY',
  priceConfigId: '',
  vendor: StationeryPrintOrderVendorEnum.joy_digital,
  productType: 'digital',
  vendorBaseSKU: '',
  priceCurrency: '',
  attributes: [],
  pricing: {
    __typename: 'StationeryFlatFeePricing',
    priceInMinorUnits: 0
  },
  variantGroups: []
};

// An automatic linking is performed from attributeId / to translations for selector text and line item text.
// Make sure stationeryPriceConfigMapping.json is up to date and you've run i18n:codegen.
// Or else your attributes or options will not render.
export const UI_MAPPING_CONFIG: ProductUIConfig = {
  productTypes: [
    {
      type: 'stationeryCard',
      mappingFunction: 'taylormap',
      supportedQuantities: new Array(40).fill(null).map((_, index) => (index + 1) * 10),
      cardTypeSKU: {
        [StationeryPaperSize.A2]: {
          envelopeSKU: A2_ENVELOPE_SKU,
          cardType: 'FlatNoteCard'
        },
        [StationeryPaperSize.A7]: {
          envelopeSKU: A7_ENVELOPE_SKU,
          cardType: 'FlatNoteCard'
        }
      }
    },
    {
      type: 'digitalCard',
      defaultQuantity: 250,
      mappingFunction: 'joydigitalmap',
      supportedQuantities: [50, 100, 250, 500, 1000]
    },
    {
      type: 'napkin',
      defaultQuantity: 100,
      mappingFunction: 'taylormap',
      supportedQuantities: [10, 20, 100]
    },
    {
      type: 'sign',
      defaultQuantity: 100,
      mappingFunction: 'taylormap',
      supportedQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    },
    {
      type: 'digital',
      supportedQuantities: [1],
      mappingFunction: 'freedigitalmap'
    }
  ],
  attributes: [
    {
      attributeId: 'foil',
      step: 'cardFront',
      sortOrder: 0,
      componentDefinitions: {
        selector: {
          componentId: 'FoilChoiceGroup'
        }
      },
      options: [
        {
          optionId: 'gold',
          value: {
            colorId: 'GOLD',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_gold.jpeg'
          }
        },
        {
          optionId: 'roseGold',
          value: {
            colorId: 'ROSEGOLD',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_rosegold.jpeg'
          }
        },
        {
          optionId: 'silver',
          value: {
            colorId: 'SILVER',
            assetThumbnailUrl: 'https://withjoy.com/media/print/foil_color_silver.jpeg'
          }
        }
      ]
    },
    {
      attributeId: 'paperType',
      sortOrder: 1,
      componentDefinitions: {
        // displayName: tAttributes.paperType.selector.displayName,
        selector: { componentId: 'PaperTypeAttribute' }
      },
      step: 'cardFront',
      options: [
        {
          optionId: 'signature',
          value: 'standard'
        },
        {
          optionId: 'smooth',
          value: 'smooth'
        },
        {
          optionId: 'pearlescent',
          value: 'pearlescent'
        },
        {
          optionId: 'doubleThick',
          value: 'doubleThick'
        }
      ]
    },
    {
      attributeId: 'shape',
      sortOrder: 2,
      componentDefinitions: {
        selector: { componentId: 'DieCutIconAttribute' }
      },
      step: 'cardFront',
      options: [
        {
          optionId: 'rectangle',
          value: 'rectangle'
        },
        {
          optionId: 'rounded',
          value: 'rounded'
        },
        {
          optionId: 'arch',
          value: 'arch'
        }
      ]
    },
    {
      attributeId: 'returnAddress',
      componentDefinitions: {
        selector: { componentId: 'SimpleTextAttribute', props: { columns: 3, minHeight: 68 } }
      },
      step: 'envelope',
      options: [
        {
          optionId: 'none',
          value: 'none',
          sortOrder: 2
        },
        {
          optionId: 'front',
          value: 'front',
          sortOrder: 0
        },
        {
          optionId: 'back',
          value: 'back',
          sortOrder: 1
        }
      ]
    },
    {
      attributeId: 'recipientAddress',
      componentDefinitions: {
        selector: { componentId: 'ColumnTextAttribute' }
      },
      step: 'recipientAddress',
      options: [
        {
          optionId: 'none',
          value: 'none',
          sortOrder: 0
        },
        {
          optionId: 'front',
          value: 'front',
          sortOrder: 1
        }
      ]
    },
    {
      attributeId: 'cardBack',
      step: 'cardBack',
      componentDefinitions: {
        selector: {
          componentId: 'none'
        }
      },
      options: [
        {
          optionId: 'free'
        }
      ]
    },
    {
      attributeId: 'delivery',
      step: 'review',
      componentDefinitions: {
        selector: {
          componentId: 'DigitalDeliveryAttribute'
        }
      },
      options: [
        {
          optionId: 'basic',
          value: 'basic'
        },
        {
          optionId: 'premium',
          value: 'premium'
        },
        {
          optionId: 'diamond',
          value: 'diamond'
        }
      ]
    }
  ],
  deliveryMethods: [
    { deliveryMethod: 'economy' as CardDelivery, priceInMinorUnits: 999 }, // $9.99,
    { deliveryMethod: 'standard' as CardDelivery, priceInMinorUnits: 1495 }, // $14.95,
    { deliveryMethod: 'express' as CardDelivery, priceInMinorUnits: 3995 }, // $39.95,
    { deliveryMethod: 'ups_ground_canada_standard' as CardDelivery, priceInMinorUnits: 3500 }, // $35.00,
    { deliveryMethod: 'ups_canada_express_saver' as CardDelivery, priceInMinorUnits: 5500 }, // $55.00,
    { deliveryMethod: 'ups_international_express_saver' as CardDelivery, priceInMinorUnits: 5500 } // 55.00,
  ]
};
